import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  DollarSign,
  ShoppingBag,
  Users,
  Package,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { formatPrice } from '../../utils/currency';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalRevenue: 0,
    todayRevenue: 0,
    totalOrders: 0,
    todayOrders: 0,
    totalCustomers: 0,
    newCustomers: 0,
    totalProducts: 0,
    lowStockProducts: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      // Fetch analytics overview
      const analyticsRes = await fetch(
        `${window.location.origin}/api/analytics/overview?period=30d`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (analyticsRes.ok) {
        const analyticsData = await analyticsRes.json();
        setStats({
          totalRevenue: analyticsData.revenue.total,
          todayRevenue: analyticsData.revenue.today,
          totalOrders: analyticsData.orders.total,
          todayOrders: analyticsData.orders.today,
          totalCustomers: analyticsData.customers.total,
          newCustomers: analyticsData.customers.new,
          totalProducts: analyticsData.products.total,
          lowStockProducts: analyticsData.products.lowStock,
        });
      }

      // Fetch recent orders
      const ordersRes = await fetch(
        `${window.location.origin}/api/orders/admin/all?page=1&limit=5`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        setRecentOrders(ordersData.orders || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const MetricCard = ({ title, value, change, icon: Icon, trend, color }) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${color}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {change && (
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
            {trend === 'up' ? (
              <ArrowUpRight className="h-3 w-3 text-green-600" />
            ) : (
              <ArrowDownRight className="h-3 w-3 text-red-600" />
            )}
            <span className={trend === 'up' ? 'text-green-600' : 'text-red-600'}>
              {change}
            </span>
            <span>from yesterday</span>
          </p>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#be1f1a]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Metrics Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Revenue"
          value={formatPrice(stats.totalRevenue, 'COP')}
          change={`+${formatPrice(stats.todayRevenue, 'COP')} today`}
          icon={DollarSign}
          trend="up"
          color="text-green-600"
        />
        <MetricCard
          title="Total Orders"
          value={stats.totalOrders}
          change={`+${stats.todayOrders} today`}
          icon={ShoppingBag}
          trend="up"
          color="text-blue-600"
        />
        <MetricCard
          title="Total Customers"
          value={stats.totalCustomers}
          change={`+${stats.newCustomers} this month`}
          icon={Users}
          trend="up"
          color="text-purple-600"
        />
        <MetricCard
          title="Total Products"
          value={stats.totalProducts}
          icon={Package}
          color="text-orange-600"
        />
      </div>

      {/* Low Stock Alert */}
      {stats.lowStockProducts > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <AlertTriangle className="h-5 w-5" />
              Low Stock Alert
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-yellow-700">
              {stats.lowStockProducts} product(s) are running low on stock.{' '}
              <Link to="/admin/products" className="font-semibold underline">
                View Products
              </Link>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Recent Orders */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Recent Orders</CardTitle>
              <CardDescription>Latest orders from your store</CardDescription>
            </div>
            <Link to="/admin/orders">
              <button className="text-sm text-[#be1f1a] hover:text-[#a71b17] font-medium flex items-center gap-1">
                View All
                <ArrowUpRight className="h-4 w-4" />
              </button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentOrders.map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:border-[#be1f1a] transition-colors"
              >
                <div className="flex-1">
                  <p className="font-semibold text-[#be1f1a]">#{order.orderNumber}</p>
                  <p className="text-sm text-gray-600">{order.customer}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(order.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                      order.status
                    )}`}
                  >
                    {order.status}
                  </span>
                  <p className="font-semibold text-gray-900 min-w-[80px] text-right">
                    {formatPrice(order.total, 'COP')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-2">
        <Link to="/admin/products">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-[#be1f1a]" />
                Manage Products
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                View and manage product listings
              </p>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/orders">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 text-[#be1f1a]" />
                Manage Orders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                View and process customer orders
              </p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
